import * as d3 from 'd3';
import Snap from 'snapsvg-cjs';
import { EVENTTYPES, StatusMapEnum } from './types';
import type {
  Callback,
  SVGSelection,
  D3Selection,
  TextSelection,
  CircleSelection,
  GroupSelection,
  ToolOptions
} from './types';

class SVGTool {
  svgNode: SVGSelection | null = null; // svg节点
  svgViewNode: D3Selection | null = null; // svg视口节点
  textNodes: TextSelection = null; // 所有文本节点
  groupNodes: GroupSelection = null; // 所有文本&圆圈的组合节点
  validTexts: number[] = []; // 不可点击的文本组合
  highlightText: string = '0'; // 高亮的文本
  private handlers: {
    [key in EVENTTYPES]?: { callback: Callback }[];
  } = {};

  constructor({ svgElement, validTexts, defaultCallout }: ToolOptions) {
    this.validTexts = validTexts;
    this.highlightText = '0';
    this.svgNode = d3.select(svgElement);
    this.svgViewNode = this.svgNode.select('g');
    this.textNodes = this.initializeTexts();
    this.initializeGroup();
    this.groupNodes = this.svgNode.selectAll('g[source="custom"]');
    if (defaultCallout) {
      this.hightLightNodesByText(String(defaultCallout));
    }
    this.initEvents();
  }

  private initEvents() {
    this.groupNodes.on('click', event => this.handleGroupClick(event.currentTarget as SVGGElement));
    this.groupNodes.on('mouseover', event =>
      this.handleGroupMouseOver(event.currentTarget as SVGGElement)
    );
    this.groupNodes?.on('mouseout', () => this.handleGroupMouseOut());
  }

  public removeEvents() {
    this.groupNodes?.on('click', null);
    this.groupNodes?.on('mouseover', null);
    this.groupNodes?.on('mouseout', null);
  }

  private notify(type: EVENTTYPES, data: any) {
    if (!type || !this.handlers[type]) return;
    this.handlers[type]?.forEach(({ callback }) => {
      callback(data);
    });
  }

  public subscribe(type: EVENTTYPES, callback: Callback) {
    this.handlers[type] = this.handlers[type] || [];
    (this.handlers[type] as { callback: Callback }[]).push({ callback });
  }

  public unsubscribe(type: EVENTTYPES) {
    this.handlers[type] = [];
  }

  public removeHandles() {
    this.handlers = {};
  }

  private removeNode() {
    this.textNodes?.remove();
    this.groupNodes?.remove();
    this.svgViewNode?.remove();
    this.svgNode?.remove();
    this.textNodes = null;
    this.groupNodes = null;
    this.svgNode = null;
    this.svgViewNode = null;
    this.validTexts = [];
  }

  /**
   * 初始化TextNodes
   * @returns 返回有效的文本节点 && 添加初始样式
   */
  private initializeTexts(): TextSelection | [] {
    if (this.svgNode) {
      return this.svgNode.selectAll('text')?.filter((_, i, nodes) => {
        const nodeItem = d3.select(nodes[i] as SVGTextElement);
        // 去除空格和换行符
        const trimStr = this.trimTextStr(nodeItem.text());

        if (/^\w{1,3}$/.test(trimStr)) {
          nodeItem.text(trimStr);
          const status = this.isDisabledText(trimStr) ? 'DISABLED' : 'DEFAULT';
          nodeItem.style('fill', StatusMapEnum[status].textFill);
          nodeItem.style('font-size', '16px');
          return true;
        }
        return false;
      });
    }
    return [];
  }

  /**
   *
   * @returns 初始化圆圈节点 && 添加初始样式
   */
  private initializeGroup() {
    this.textNodes.each((_, i, nodes) => {
      const text = d3.select(nodes[i] as SVGTextElement).text();
      const status = this.isDisabledText(text) ? 'DISABLED' : 'DEFAULT';
      this.createCircleAndG(
        nodes[i] as SVGTextElement,
        StatusMapEnum[status].circleStroke,
        StatusMapEnum[status].flag
      );
    });
  }

  /**
   * @desc 销毁事件监听 && 节点
   */
  public destroy() {
    this.removeEvents();
    this.removeHandles();
    this.removeTooltip();
    this.removeNode();
  }

  private handleGroupClick(target: SVGGElement) {
    const groupNode = d3.select(target);
    const text: string = groupNode.select('text').text();
    // 如果已经选中||禁止选择,不再触发点击事件
    if (this.highlightText === text || this.isDisabledText(text)) return;
    this.hightLightNodesByText(text);
    this.notify(EVENTTYPES.TEXTCLICK, text); // 通知外部点击事件
  }

  private handleGroupMouseOver(target: SVGGElement) {
    if (!target) return;
    const groupNode = d3.select(target);
    const text = groupNode.select('text').text();
    const status = this.isDisabledText(text) ? 'DISABLED_HOVER' : 'HOVER';
    const circle = groupNode.select('circle');
    const newCircle = circle.node()?.cloneNode(true) as SVGCircleElement;
    newCircle.setAttribute('stroke', StatusMapEnum[status].circleStroke);
    newCircle.setAttribute('stroke-width', '2');
    newCircle.setAttribute('r', StatusMapEnum[status].circleRadius);
    newCircle.setAttribute('flag', StatusMapEnum[status].flag);
    groupNode.node()?.insertBefore(newCircle, circle.node());
    // 如果是不可点击的文本，显示 tooltip
    if (this.isDisabledText(text)) this.showTooltip(circle);
  }

  private handleGroupMouseOut() {
    this.removeCircle('hover');
    this.removeTooltip();
  }

  private showTooltip(circle: CircleSelection) {
    const bbox = circle.node()?.getBBox();
    if (!bbox) return;

    // 创建 tooltip 的 <g> 容器
    const tooltipGroup = this.svgViewNode
      .append('g')
      .attr('class', 'tooltip-group')
      .attr('transform', `translate(${bbox.x + bbox.width / 2}, ${bbox.y - 44})`);

    // 创建圆角矩形
    tooltipGroup
      .append('rect')
      .attr('x', -92) // 矩形中心对齐
      .attr('y', 0)
      .attr('width', 184)
      .attr('height', 36)
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('fill', 'white')
      .attr('stroke', 'rgba(23, 31, 38, 0.3)')
      .attr('stroke-width', '1');

    // 创建倒三角形
    tooltipGroup
      .append('polygon')
      .attr('points', '-4,36 4,36 0,39')
      .attr('fill', 'white')
      .attr('stroke', 'rgba(23, 31, 38, 0.3)')
      .attr('stroke-width', '1')
      .attr('stroke-linejoin', 'round');

    // 覆盖拼接处
    tooltipGroup
      .append('polygon')
      .attr('points', '-4,36 4,36 0,39')
      .attr('fill', 'white')
      .attr('stroke', 'white')
      .attr('stroke-width', '1')
      .attr('stroke-linejoin', 'round')
      .attr('transform', 'translate(0, -1)');

    // 添加文本
    tooltipGroup
      .append('text')
      .attr('x', 0)
      .attr('y', 22)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('font-size', '13px')
      .text('该部位暂未配置适用备件信息');
  }

  private removeTooltip() {
    this.svgViewNode?.selectAll('.tooltip-group').remove();
  }

  /**
   *
   * @param selectTexts 选中的Text
   * @returns 选中相同Text内容的text&circle节点
   */
  public outsideHighlight(selectText: string) {
    this.hightLightNodesByText(selectText);
  }

  private hightLightNodesByText(selectText: string) {
    this.highlightText = selectText;
    const selectedColor = StatusMapEnum['SELECTED'];
    const defaultColor = StatusMapEnum['DEFAULT'];

    this.groupNodes?.each((_, i, nodes) => {
      const item = d3.select(nodes[i] as SVGGElement);
      const currentText = item.attr('textVal');
      if (this.isDisabledText(currentText)) return; // 跳过不可点击的文本
      if (selectText === currentText) {
        item.select('text').style('fill', selectedColor.textFill);
        item.select('circle[flag="default"]').attr('fill', selectedColor.circleFill);
        item.select('circle[flag="default"]').attr('stroke', selectedColor.circleStroke);
      } else {
        item.select('text').style('fill', defaultColor.textFill);
        item.select('circle[flag="default"]').attr('fill', defaultColor.circleFill);
        item.select('circle[flag="default"]').attr('stroke', defaultColor.circleStroke);
      }
    });
  }

  private createCircleAndG(target: SVGTextElement, stroke: string, flag: string) {
    const bbox = Snap(target).getBBox(); // 获得元素变换后的坐标
    const textVal = target.textContent as string;
    if (bbox.width && bbox.height) {
      const circle = this.createCircle(bbox.cx, bbox.cy, stroke, flag, textVal);
      // 创建一个新的 <g> 标签
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('textVal', textVal);
      g.setAttribute('source', 'custom');
      g.style.cursor = this.isDisabledText(textVal) ? 'not-allowed' : 'pointer';
      // 将 circle 和 target 添加到 <g> 标签中
      g.appendChild(circle);
      g.appendChild(target.cloneNode(true));
      this.svgViewNode.node()?.appendChild(g);
    }
  }

  private createCircle(
    cx: number,
    cy: number,
    stroke: string,
    flag: string,
    textVal: string
  ): SVGCircleElement {
    const r = StatusMapEnum['DEFAULT'].circleRadius,
      circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('flag', flag);
    circle.setAttribute('cx', cx.toString());
    circle.setAttribute('cy', cy.toString());
    circle.setAttribute('r', r);
    circle.setAttribute('stroke', stroke);
    circle.setAttribute('stroke-width', '1.2');
    circle.setAttribute('fill', StatusMapEnum['DEFAULT'].circleFill);
    circle.setAttribute('textVal', textVal);
    circle.setAttribute('source', 'custom');
    return circle;
  }

  private removeCircle(flag: string) {
    this.svgViewNode?.selectAll('circle[flag="' + flag + '"]').remove();
  }

  /**
   *
   * @desc 计算元素相对于整个 SVG 容器的坐标
   */
  private getNodeSize(node: SVGCircleElement) {
    const bbox = node.getBBox(),
      ctm = node.getCTM(),
      nodeClientRect = node.getBoundingClientRect(),
      svgClientRect = this.svgNode?.node()?.getBoundingClientRect() || { left: 0, top: 0 };
    return {
      x: nodeClientRect.left - svgClientRect.left,
      y: nodeClientRect.top - svgClientRect.top,
      width: bbox.width,
      height: bbox.height,
      scale: ctm?.a ?? 1
    };
  }

  private trimTextStr(str: string): string {
    return str.replace(/[ ]/g, '').replace(/[\r\n]/g, '');
  }

  private isDisabledText(text: string) {
    if (!this.validTexts) return true;
    return !this.validTexts?.includes(Number(text));
  }
}

export default SVGTool;
